import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useBankAccountReasonService from '../../app/services/hooks/useBankAccountReasonService';
import { BankAccountReason, BankAccountReasonFilters } from '../../app/models/BankAccountReason';
import BankAccountReasonDataTable from './components/tables/BankAccountReasonDataTable';
import BankAccountReasonCreate from './components/BankAccountReasonCreate';
import BankAccountReasonEdit from './components/BankAccountReasonEdit';
import { useHistory } from 'react-router-dom';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Motivo de cuenta',
        url: '/BankAccountReasons',
        isActive: true
    }
];

const BankAccountReasons = () => {
    const history = useHistory();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { fetchingGetBankAccountReasons, getBankAccountReasons, deleteBankAccountReason, activeBankAccountReason } = useBankAccountReasonService();
    const [bankAccountReasons, setBankAccountReasons] = useState<BankAccountReason[]>([]);

    const [bankAccountReasonIdEdit, setBankAccountReasonIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const { navigationPage } = useNavigationPage();

    const [bankAccountReasonFilter, setBankAccountReasonFilter] = useState<BankAccountReasonFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        reloadTable();
    }, [bankAccountReasonFilter]);

    const getAllBankAccountReasons = () => {
        getBankAccountReasons(bankAccountReasonFilter, {
            onSuccess: (response: ServiceResponse) => {
                setBankAccountReasons(response.data.bank_account_reasons	);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (bankAccountReasonId: number) => {
        setShowingEdit(true);
        setBankAccountReasonIdEdit(bankAccountReasonId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setBankAccountReasonIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllBankAccountReasons();
    };

    const destroy = (bankAccountReasonId: number) => {
        const _text = 'Está a punto de eliminar el motivo de cuenta #' + bankAccountReasonId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando motivo de cuenta...');
                        deleteBankAccountReason(bankAccountReasonId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El motivo de cuenta no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const active = (bankAccountReasonId: number, active: boolean) => {
        activeBankAccountReason(bankAccountReasonId, active, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Motivo de cuenta" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo motivo de cuenta" />}
                />
                <BankAccountReasonDataTable
                    bankAccountReasons={bankAccountReasons}
                    edit={showEdit}
                    destroy={destroy}
                    active={active}
                    loading={fetchingGetBankAccountReasons}
                    filter={bankAccountReasonFilter}
                    setFilter={setBankAccountReasonFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear motivo de cuenta"
                    backdrop={true}
                    showFooter={false}
                >
                    <BankAccountReasonCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Banco"
                    backdrop={true}
                    showFooter={false}
                >
                    <BankAccountReasonEdit
                        bankAccountReasonId={bankAccountReasonIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default BankAccountReasons;
